import clsx from "clsx";
import Markdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";

import type { CompletedQuestion } from "@/lib/types";

import { useStore } from "@/lib/store";

interface CompletedQuestionBlockProps {
  history: CompletedQuestion[];
}

/**
 * Renders a completed Q&A exchange inline in the conversation.
 *
 * Each entry shows: partial response (if any) -> questions asked -> answers given.
 */
export function CompletedQuestionBlock({ history }: CompletedQuestionBlockProps): JSX.Element {
  const theme = useStore((state) => state.theme);

  return (
    <div className="mb-3 space-y-2">
      {history.map((entry, ei) => (
        <div key={ei}>
          {/* Partial response the AI produced before asking */}
          {entry.partialResponse && (
            <div
              className={clsx(
                "prose prose-sm mb-2 max-w-none rounded-lg border border-cyber-border bg-cyber-surface p-3 text-sm text-cyber-text prose-headings:my-3 prose-p:my-2 prose-a:text-cyber-cyan prose-code:rounded prose-code:bg-cyber-bg prose-code:px-1 prose-code:text-cyber-cyan prose-pre:my-2 prose-ol:my-2 prose-ul:my-2 prose-li:my-0",
                theme === "cyber" && "prose-invert",
              )}
            >
              <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                {entry.partialResponse}
              </Markdown>
            </div>
          )}

          {/* Q&A block */}
          <div className="rounded-lg border border-cyber-cyan/20 bg-cyber-cyan/5 px-3 py-2.5 text-xs">
            <div className="mb-2 text-[10px] font-medium uppercase tracking-wider text-cyber-cyan/70">
              Q&A
            </div>
            {entry.questions.map((q, qi) => {
              const answer = entry.answers[qi];
              const answerText = answer?.length ? answer.join(", ") : "—";

              return (
                <div key={qi} className={qi > 0 ? "mt-3 border-t border-cyber-border/40 pt-3" : ""}>
                  {q.header && <div className="mb-0.5 font-medium text-cyber-text">{q.header}</div>}
                  <div className="text-cyber-muted">{q.question}</div>

                  {q.options.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {q.options.map((opt) => {
                        const wasChosen = answer?.includes(opt.label);
                        return (
                          <span
                            key={opt.label}
                            className={`inline-block rounded border px-2 py-0.5 ${
                              wasChosen
                                ? "border-cyber-cyan/40 bg-cyber-cyan/15 text-cyber-cyan"
                                : "border-cyber-border/50 text-cyber-muted/60"
                            }`}
                          >
                            {opt.label}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  <div className="mt-1.5 flex items-baseline gap-1.5">
                    <span className="text-[10px] uppercase text-cyber-cyan/60">Answer:</span>
                    <span className="font-medium text-cyber-text">{answerText}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
